import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, IconButton, Snackbar, Alert,
  Skeleton,
} from "@mui/material";
import { MdVisibility } from "react-icons/md";

const GRN_STATUSES = {
  completed: { label: "Completed", color: "bg-green-100 text-green-800" },
  partial: { label: "Partial", color: "bg-yellow-100 text-yellow-800" },
  pending: { label: "Pending", color: "bg-gray-100 text-gray-800" },
};

const GRNList = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchGRNs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getAllGRN?page=${page + 1}&limit=${rowsPerPage}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setGrns(data.grns || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to fetch goods receipts", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGRNs();
  }, [page, rowsPerPage]);

  const getStatusBadge = (status) => {
    const config = GRN_STATUSES[status] || { label: status || "N/A", color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <h2 className="text-xl font-semibold text-gray-800">Goods Received Notes</h2>
        <button
          onClick={() => navigate("/GRN/New")}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          + New GRN
        </button>
      </div>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600 }}>GRN #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>PO #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Received By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Received Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : grns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No goods received notes found</p>
                  </TableCell>
                </TableRow>
              ) : (
                grns.map((grn) => (
                  <TableRow
                    key={grn._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/GRN/View/${grn._id}`)}
                  >
                    <TableCell>
                      <span className="font-medium text-primary">{grn.grnNumber}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-primary">{grn.purchaseOrder?.poNumber || "N/A"}</span>
                    </TableCell>
                    <TableCell>{grn.vendor?.name || grn.purchaseOrder?.vendor?.name || "N/A"}</TableCell>
                    <TableCell>{grn.receivedBy?.name || "N/A"}</TableCell>
                    <TableCell>
                      {grn.receivedDate
                        ? new Date(grn.receivedDate).toLocaleDateString()
                        : new Date(grn.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{getStatusBadge(grn.status)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/GRN/View/${grn._id}`)}
                      >
                        <MdVisibility className="text-primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default GRNList;
